import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiRoutes } from '@/api/routes';
import { useApiErrorMessage } from '@/api/use-api-error';
import { Alert, AlertDescription } from '@/design/components/alert';
import { Button } from '@/design/components/button';
import { Input } from '@/design/components/input';
import { Label } from '@/design/components/label';
import { AuthCard } from '@/features/auth/auth-card';
import { useSessionContext } from '@/features/auth/session';

/**
 * The forced first-login password change (Section 1.7).
 *
 * An account created by an administrator arrives with a temporary password that was shown to them
 * once and quite possibly read aloud. Until it is replaced the session is behind the
 * `password_change` gate and reaches nothing else, so this screen is the whole of what that session
 * can do — which is why it is a screen rather than a dialog over an application the operator cannot
 * use.
 *
 * **The repeat field is checked here and nowhere else.** The server takes one new password, because
 * a second copy of the same secret is not something an API should carry; the mismatch is a typing
 * mistake and belongs where the typing happens.
 *
 * **The session in the response replaces the one held.** Changing a password rotates the session
 * id, so the CSRF token the client is holding is bound to an identifier that no longer exists;
 * without adopting the fresh session the very next write would be refused.
 */
export function PasswordChangePage(): ReactNode {
  const { t } = useTranslation();
  const { client, adopt } = useSessionContext();
  const describe = useApiErrorMessage();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeated, setRepeated] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(): Promise<void> {
    if (newPassword !== repeated) {
      setFailure(t('auth.passwordsDoNotMatch'));
      return;
    }
    setFailure(null);
    setSubmitting(true);
    try {
      adopt(
        await client.request(apiRoutes.authChangePassword, {
          body: { currentPassword, newPassword },
        }),
      );
    } catch (error) {
      setFailure(describe(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard title={t('auth.passwordChangeTitle')} description={t('auth.passwordChangeSubtitle')}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        noValidate
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="current-password">{t('auth.currentPassword')}</Label>
          <Input
            id="current-password"
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(event) => {
              setCurrentPassword(event.target.value);
            }}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="new-password">{t('auth.newPassword')}</Label>
          <Input
            id="new-password"
            type="password"
            autoComplete="new-password"
            required
            value={newPassword}
            onChange={(event) => {
              setNewPassword(event.target.value);
            }}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="repeat-password">{t('auth.repeatPassword')}</Label>
          <Input
            id="repeat-password"
            type="password"
            autoComplete="new-password"
            required
            value={repeated}
            onChange={(event) => {
              setRepeated(event.target.value);
            }}
          />
        </div>

        {failure !== null && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{failure}</AlertDescription>
          </Alert>
        )}

        <Button type="submit" disabled={submitting}>
          {t('auth.changePassword')}
        </Button>
      </form>
    </AuthCard>
  );
}
